import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden/Hidden";
import Img, { ImgProps } from "@insite/mobius/Img";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Constant from "@tSurya/Constants/Constant";
import { cartLineCardExpandedStyles } from "@tSurya/Overrides/Components/CartLineCardExpanded";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import React from "react";
import { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    count: number;
}

const mapStateToProps = (state: ApplicationState) => ({
    currentPage: getCurrentPage(state).type,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface RollPadServiceStyle {
    container?: GridContainerProps;
    columnContainer?: InjectableCss;
    imageColumn?: InjectableCss;
    productColumn?: InjectableCss;
    productDetails?: InjectableCss;
    unitPriceColumn?: InjectableCss;
    qtyColumn?: InjectableCss;
    totalPriceColumn?: InjectableCss;
    deleteColumn?: InjectableCss;
    serviceLabel?: InjectableCss;
    titleLabel?: InjectableCss;
    priceLabel?: InjectableCss;
    priceValue?: InjectableCss;
    totalPriceValue?: InjectableCss;
    mobileBottomSection?: InjectableCss;
    mobilePriceSection?: InjectableCss;
    quantityInput?: TextFieldPresentationProps;
    img?: ImgProps;
    checkoutReviewAndSubmitContainer?: GridContainerProps;
    checkoutReviewAndSubmitLabelGridItem?: GridItemProps;
    checkoutReviewAndSubmitQuantity?: GridItemProps;
    checkoutReviewAndSubmitLabel: InjectableCss;
    checkoutReviewAndSubmitProductImage?: GridItemProps;
    subtotalPriceGridItem?: GridItemProps;
}

const RollPadServiceStyle: RollPadServiceStyle = {
    container: {
        gap: 0,
        css: css`
            margin-bottom: 0;
            padding: 0;
            position: relative;
            font-family: ${TYPOGRAPHY.fontFamily.base} !important;
        `,
    },
    columnContainer: {
        // Grid and gap are set page-aware on the element so they can mirror each page's cart line.
        css: css`
            display: grid;
            width: 100%;
            padding: 16px 0;
            align-items: flex-start;
            border-bottom: 1px solid #DDDDDD;
            font-family: ${TYPOGRAPHY.fontFamily.base} !important;
        `,
    },
    imageColumn: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                grid-row-start: 1;
                                grid-column-start: 1;
                                grid-column-end: 2;
                                grid-row-end: 5;
                            `,
                        ],
                        "max",
                    )}

            img {
                width: 115px;
                max-width: 115px;
                flex-shrink: 0;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            null,
                            css`
                                width: 62px !important;
                                max-width: 62px !important;
                            `,
                            null
                        ],
                        "max",
                    )}
            }
        `
    },
    productColumn: {
        css: css`
            display: flex;
            align-items: flex-start;
            gap: 15px;

            ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                grid-row-start: 1;
                                grid-column-start: 2;
                                grid-column-end: 5;
                                gap: 10px;
                            `,
                        ],
                        "max",
                    )}
        `,
    },
    productDetails: {
        css: css`
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 8px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            padding-left: 2px;
                            padding-right: 30px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    unitPriceColumn: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            text-align: left;

            ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                grid-row-start: 3;
                                grid-column-start: 3;
                                grid-column-end: 4;
                                align-items: center;
                                text-align: center;
                            `,
                        ],
                        "max",
                    )}

            fieldset {
                border: none;
                padding: 0;
                margin: 0;
            }

            legend {
                display: none;
                text-transform: capitalize !important;
                font-weight: 400;
                font-family: ${TYPOGRAPHY.fontFamily.base};
                padding-left: 0;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                display: block;
                                font-size: ${TYPOGRAPHY.size.smaller};
                                color: #494745;
                            `,
                        ],
                        "max",
                    )}
            }
        `,
    },
    qtyColumn: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            justify-content: center;

            ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                grid-row-start: 3;
                                grid-column-start: 2;
                                grid-column-end: 3;
                                align-items: flex-start;
                                text-align: left;
                                justify-content: flex-start;
                            `,
                        ],
                        "max",
                    )}
        `,
    },
    totalPriceColumn: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            text-align: right;
            white-space: nowrap;

            ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                grid-row-start: 3;
                                grid-column-start: 4;
                                grid-column-end: 5;
                                flex-direction: column;
                                align-items: flex-end;
                                text-align: right;
                            `,
                        ],
                        "max",
                    )}

            fieldset {
                border: none;
                padding: 0;
                margin: 0;
            }

            legend {
                display: none;
                text-transform: capitalize !important;
                font-weight: 400;
                font-family: ${TYPOGRAPHY.fontFamily.base};
                padding-left: 0;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                display: block;
                                font-size: ${TYPOGRAPHY.size.smaller};
                                color: #494745;
                            `,
                        ],
                        "max",
                    )}
            }
        `,
    },
    deleteColumn: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: flex-start;

            ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                display: none;
                            `,
                        ],
                        "max",
                    )}
        `,
    },
    serviceLabel: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.title};
            font-style: normal;
            font-weight: ${TYPOGRAPHY.weight.bold};
            font-size: ${TYPOGRAPHY.size.base};
            color: ${COLORS.primary.base};
        `,
    },
    titleLabel: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.title};
            font-style: normal;
            font-weight: ${TYPOGRAPHY.weight.bold};
            font-size: ${TYPOGRAPHY.size.base};
            color: ${COLORS.primary.base};
        `,
    },
    priceLabel: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.title};
            font-style: normal;
            font-weight: ${TYPOGRAPHY.weight.base};
            font-size: ${TYPOGRAPHY.size.smaller};
            line-height: ${TYPOGRAPHY.lineHeight.base};
            color: #494745;
            margin-bottom: 4px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            font-size: ${TYPOGRAPHY.size.smaller};
                        `,
                    ],
                    "max",
                )}
        `,
    },
    priceValue: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.title};
            font-style: normal;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-size: ${TYPOGRAPHY.size.base};
            line-height: ${TYPOGRAPHY.lineHeight.xbase};
            color: ${COLORS.text.main};
            white-space: nowrap;
        `,
    },
    totalPriceValue: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.title};
            font-style: normal;
            font-weight: ${TYPOGRAPHY.weight.bold};
            font-size: ${TYPOGRAPHY.size.base};
            line-height: ${TYPOGRAPHY.lineHeight.xbase};
            color: ${COLORS.text.main};
        `,
    },
    quantityInput: {
        // Pinned instead of themed: the read-only quantity box must render identically on every brand.
        css: css`
            text-align: center;
            border: none !important;
            border-radius: 0 !important;
            width: 134px !important;
            height: 40px !important;
            font-size: 16px !important;
            color: #333333 !important;
            background-color: #e5e5e5 !important;

            && {
                height: 40px !important;
            }

            /* Remove number input spinner buttons */
            &::-webkit-outer-spin-button,
            &::-webkit-inner-spin-button {
                -webkit-appearance: none;
                margin: 0;
            }

            /* Firefox */
            &[type=number] {
                -moz-appearance: textfield;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            width: 70px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    img: {
        css: css`
            height: auto;
            width: 100%;
        `,
    },
    checkoutReviewAndSubmitContainer: {
        gap: 20,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 1rem 0;
        `,
    },
    checkoutReviewAndSubmitProductImage: {
        width: 2,
        printWidth: 1,
        css: css`
            overflow: hidden;
            display: flex;

            img {
                max-width: 100%;
                height: auto;
            }

            @media print {
                max-height: 83px;
                max-width: 83px;
                padding: 0;
            }
        `,
    },
    checkoutReviewAndSubmitLabelGridItem: {
        css: css`
            flex-direction: column;
        `,
        width: [12, 12, 5, 5, 5],
        printWidth: 6,
    },
    checkoutReviewAndSubmitQuantity: {
        width: [12, 12, 3, 3, 3],
        css: css`
            flex-direction: column;
        `,
    },
    checkoutReviewAndSubmitLabel: {
        css: css`
            font-size: 10px;
            font-weight: 600;
        `,
    },
    subtotalPriceGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            flex-direction: column;
        `,
    },
};

const style = RollPadServiceStyle;
const styles = cartLineCardExpandedStyles;

const CartLineRollService: FC<Props> = ({ count, currentPage }) => {
    const ImgUrl = siteMessage("roll_service_imgUrl") as string;
    const isCartPage = currentPage === "CartPage";
    const quantityHeadingStyles = styles.quantity?.readOnlyQuantityText?.extendedStyles?.heading;
    const totalPriceHeadingStyles = styles.extendedUnitNetPrice?.heading;

    const CartLineDetails = (
        <StyledWrapper 
            {...style.columnContainer}
            css={css`
                ${style.columnContainer?.css}
                grid-template-columns: ${isCartPage ? "115px minmax(280px, 340px) 1fr 1fr 1fr 24px" : "115px 32% 1fr 1fr 1fr"};
                gap: ${isCartPage ? "20px" : "16px"};

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                grid-template-columns: ${isCartPage ? "62px 1fr 1fr" : "62px 1fr 1fr 1fr"} !important;
                                gap: 12px !important;
                            `,
                            css`
                                grid-template-columns: ${isCartPage ? "70px 270px 1fr 1fr 1fr 21px" : "62px 32% 1fr 1fr 1fr"};
                                gap: 12px;
                            `,
                        ],
                        "max",
                    )}
            `}
        >
            {/* Image Column */}
            <StyledWrapper {...style.imageColumn}>
                <Img {...style.img} src={ImgUrl} width="115" height="140" alt="Roll Pad Service" />
            </StyledWrapper>

            {/* Product Column */}
            <StyledWrapper
                {...style.productColumn}
                css={css`
                    ${style.productColumn?.css}
                    ${isCartPage &&
                    css`
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(
                                theme,
                                [
                                    null,
                                    css`
                                        grid-column-end: 4;
                                    `,
                                ],
                                "max",
                            )}
                    `}
                `}
            >
                <StyledWrapper {...style.productDetails}>
                    <StyledWrapper {...style.serviceLabel}>Service</StyledWrapper>
                    <StyledWrapper {...style.titleLabel}>Roll Pad Service</StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>

            {/* Unit Price Column */}
            <StyledWrapper
                {...style.unitPriceColumn}
                css={css`
                    ${style.unitPriceColumn?.css}
                    ${!isCartPage &&
                    css`
                        align-items: flex-start;
                    `}
                    ${isCartPage &&
                    css`
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(
                                theme,
                                [
                                    null,
                                    css`
                                        grid-row-start: 3;
                                        grid-column-start: 2;
                                        grid-column-end: 3;
                                        align-items: flex-start;
                                        text-align: left;
                                    `,
                                ],
                                "max",
                            )}
                    `}
                `}
            >
                <fieldset>
                    <legend>Unit Price</legend>
                    <Typography {...style.priceValue}>{siteMessage("rollServiceUnitCharge")}</Typography>
                </fieldset>
            </StyledWrapper>

            {/* Quantity Column */}
            <StyledWrapper
                {...style.qtyColumn}
                css={css`
                    ${style.qtyColumn?.css}
                    ${isCartPage &&
                    css`
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(
                                theme,
                                [
                                    null,
                                    css`
                                        grid-row-start: 2;
                                        grid-column-start: 2;
                                        grid-column-end: 4;
                                    `,
                                ],
                                "max",
                            )}
                    `}
                `}
            >
                {isCartPage ? (
                    <TextField {...style.quantityInput} type="number" value={count} disabled />
                ) : (
                    <>
                        <Typography 
                            as="p" 
                            {...quantityHeadingStyles}
                            data-test-selector="heading"
                            css={css`
                                ${quantityHeadingStyles?.css}
                                display: none;
                                
                                ${({ theme }: { theme: BaseTheme }) =>
                                    breakpointMediaQueries(
                                        theme,
                                        [
                                            null,
                                            css`
                                                display: block;
                                            `,
                                        ],
                                        "max",
                                    )}
                            `}
                        >
                            Quantity
                        </Typography>
                        <Typography {...style.priceValue}>{count}</Typography>
                    </>
                )}
            </StyledWrapper>

            {/* Total Price Column */}
            <StyledWrapper 
                {...style.totalPriceColumn}
                css={css`
                    ${style.totalPriceColumn?.css}
                    ${!isCartPage && css`
                        align-items: flex-end;
                        text-align: right;
                    `}
                    ${isCartPage &&
                    css`
                        align-items: center;
                        text-align: center;

                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(
                                theme,
                                [
                                    null,
                                    css`
                                        grid-row-start: 3;
                                        grid-column-start: 3;
                                        grid-column-end: 4;
                                        align-items: flex-end;
                                        text-align: right;
                                    `,
                                ],
                                "max",
                            )}
                    `}
                `}
            >
                <fieldset>
                    <Typography
                        as="p"
                        variant="legend"
                        {...totalPriceHeadingStyles}
                        data-test-selector="heading"
                        css={css`
                            ${totalPriceHeadingStyles?.css}
                            display: none !important;

                            ${({ theme }: { theme: BaseTheme }) =>
                                breakpointMediaQueries(
                                    theme,
                                    [
                                        null,
                                        css`
                                            display: block !important;
                                            ${isCartPage &&
                                            css`
                                                color: #494745;
                                                font-size: ${TYPOGRAPHY.size.smaller};
                                                font-family: "Mulish";
                                                font-weight: 400;
                                                line-height: 24px;
                                            `}
                                        `,
                                    ],
                                    "max",
                                )}
                        `}
                    >
                        {isCartPage ? "Total" : "Total Price"}
                    </Typography>
                    <Typography {...style.totalPriceValue}>
                        ${(count * Constant.rollServiceUnitPrice).toFixed(2)}
                    </Typography>
                </fieldset>
            </StyledWrapper>

            {/* Reserves the cart line's trash column so the columns line up; roll pad has nothing to remove. */}
            {isCartPage && <StyledWrapper {...style.deleteColumn} />}
        </StyledWrapper>
    );

    return (
        <>
            {!isCartPage &&
            currentPage !== "CheckoutReviewAndSubmitPage" &&
            currentPage !== "OrderConfirmationPage" ? (
                <GridContainer {...style.checkoutReviewAndSubmitContainer}>
                    <GridItem {...style.checkoutReviewAndSubmitProductImage}>
                        <Img src={ImgUrl} width="120" height="140" alt="Roll Pad Service" />
                    </GridItem>
                    <GridItem {...styles.orderLineInfoGridItem}>
                        <GridContainer {...styles.orderLineInfoContainer}>
                            <GridItem {...style.checkoutReviewAndSubmitLabelGridItem}>
                                <StyledWrapper style={{ fontSize: "13px", fontWeight: 600, color: "#866A5B" }}>
                                        Service{" "}
                                </StyledWrapper>
                                <StyledWrapper style={{ color: "#866A5B" }}> Roll Pad Service </StyledWrapper>
                            </GridItem>
                            <GridItem {...styles.productPriceAndQuantityGridItem}>
                                <GridContainer {...styles.productPriceAndQuantityContainer}>
                                    <GridItem {...styles.priceGridItem}>
                                        <StyledWrapper {...style.checkoutReviewAndSubmitLabel}>
                                            UNIT PRICE
                                        </StyledWrapper>
                                        <StyledWrapper style={{ fontWeight: 700 }}>
                                            {siteMessage("rollServiceUnitCharge")}
                                        </StyledWrapper>
                                    </GridItem>
                                    <GridItem {...style.checkoutReviewAndSubmitQuantity}>
                                        <StyledWrapper {...style.checkoutReviewAndSubmitLabel}>QUANTITY</StyledWrapper>
                                        <StyledWrapper>{count}</StyledWrapper>
                                    </GridItem>
                                    <GridItem {...style.subtotalPriceGridItem}>
                                        <StyledWrapper {...style.checkoutReviewAndSubmitLabel}>Subtotal</StyledWrapper>
                                        <StyledWrapper style={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                                            ${(count * Constant.rollServiceUnitPrice).toFixed(2)}
                                        </StyledWrapper>
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            ) : (
                <GridContainer {...style.container}>{CartLineDetails}</GridContainer>
            )}
        </>
    );
};

export default connect(mapStateToProps)(CartLineRollService);
